import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AccountAnalyticsBasicDetailsV2.graphql';
import { Query, QueryaccountAnalyticsBasicDetailsV2Args } from '../../__generated__/globalTypes';

export const useAccountAnalyticsBasicDetailsV2Query = (
  options?: QueryHookOptions<Query, QueryaccountAnalyticsBasicDetailsV2Args>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryaccountAnalyticsBasicDetailsV2Args>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAccountAnalyticsBasicDetailsV2LazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryaccountAnalyticsBasicDetailsV2Args>
): QueryResult<Query, QueryaccountAnalyticsBasicDetailsV2Args> & {
  getAccountAnalyticsBasicDetailsV2: LazyQueryExecFunction<Query, QueryaccountAnalyticsBasicDetailsV2Args>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAccountAnalyticsBasicDetailsV2, result] = useLazyQuery<Query, QueryaccountAnalyticsBasicDetailsV2Args>(
    QUERY,
    {
      onError: (error) => {
        enqueueSnackbar(t(error.message), { variant: 'error' });
      },
      ...options
    }
  );

  return { getAccountAnalyticsBasicDetailsV2, ...result };
};

export const useAccountAnalyticsBasicDetailsV2PromiseQuery = () => {
  const client = useApolloClient();

  const getAccountAnalyticsBasicDetailsV2 = useCallback(
    (options?: Omit<QueryOptions<QueryaccountAnalyticsBasicDetailsV2Args, Query>, 'query'>) =>
      client.query<Query, QueryaccountAnalyticsBasicDetailsV2Args>({ ...options, query: QUERY }),
    [client]
  );

  return { getAccountAnalyticsBasicDetailsV2 };
};
// Suspense Query Hooks
export const useAccountAnalyticsBasicDetailsV2SuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryaccountAnalyticsBasicDetailsV2Args>
) => {
  const result = useSuspenseQuery<Query, QueryaccountAnalyticsBasicDetailsV2Args>(QUERY, {
    ...options
  });

  return result;
};
